import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';

type PaginationItem = number | 'ellipsis';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule],
  template: `
    <footer class="ts-pagination" *ngIf="total > 0" [attr.aria-label]="ariaLabel">
      <span class="range">{{ rangeStart }}&ndash;{{ rangeEnd }} de {{ total }} {{ itemLabel }}</span>
      <nav>
        <button type="button" [disabled]="disabled || page <= 1" (click)="select(1)" title="Primera página" aria-label="Primera página"><i class="bi bi-chevron-double-left"></i></button>
        <button type="button" [disabled]="disabled || page <= 1" (click)="select(page - 1)" title="Página anterior" aria-label="Página anterior"><i class="bi bi-chevron-left"></i></button>
        <ng-container *ngFor="let item of items">
          <button type="button" *ngIf="isNumber(item)" class="number" [class.active]="item === page" [disabled]="disabled" (click)="select(item)" [attr.aria-current]="item === page ? 'page' : null">{{ item }}</button>
          <span *ngIf="!isNumber(item)" class="ellipsis" aria-hidden="true">&hellip;</span>
        </ng-container>
        <button type="button" [disabled]="disabled || page >= pages" (click)="select(page + 1)" title="Página siguiente" aria-label="Página siguiente"><i class="bi bi-chevron-right"></i></button>
        <button type="button" [disabled]="disabled || page >= pages" (click)="select(pages)" title="Última página" aria-label="Última página"><i class="bi bi-chevron-double-right"></i></button>
      </nav>
    </footer>
  `,
  styles: [`
    :host{display:block;align-self:stretch;margin-top:auto;padding-top:16px}.ts-pagination{position:relative;display:flex;justify-content:space-between;align-items:center;gap:12px;min-height:44px;box-sizing:border-box;padding:6px 10px;border:1px solid var(--ts-border-strong);border-radius:11px;background:var(--ts-bg-navy-900-3)}.range{color:var(--ts-text-sky-350-11);font-size:.69rem;white-space:nowrap}.ts-pagination nav{display:flex;align-items:center;gap:3px}.ts-pagination button{display:grid;place-items:center;min-width:30px;height:30px;margin:0;padding:0;border:1px solid transparent;border-radius:7px;color:var(--ts-text-sky-250-24);background:transparent;font-size:.68rem;cursor:pointer}.ts-pagination button:hover:not(:disabled){color:var(--ts-text);border-color:var(--ts-border-navy-700-5);background:var(--ts-bg-navy-850-13)}.ts-pagination button.active{color:var(--ts-on-primary);border-color:var(--ts-border-blue-450-3);background:linear-gradient(135deg,var(--ts-primary),var(--ts-bg-indigo-350-6));box-shadow:0 4px 11px var(--ts-shadow-blue-400-a18)}.ts-pagination button:disabled{opacity:.35;cursor:not-allowed}.ellipsis{display:grid;place-items:center;min-width:21px;color:var(--ts-text-sky-450-17)}@media(max-width:760px){:host{padding-top:13px}.ts-pagination{align-items:stretch;flex-direction:column;padding:7px 9px}.range{text-align:center}.ts-pagination nav{justify-content:center;flex-wrap:wrap}}
  `],
})
export class PaginationComponent {
  @Input() page = 1;
  @Input() pages = 1;
  @Input() total = 0;
  @Input() limit = 10;
  @Input() disabled = false;
  @Input() itemLabel = 'registros';
  @Input() ariaLabel = 'Paginación';
  @Output() pageChange = new EventEmitter<number>();

  @HostBinding('class.short-page')
  get shortPage(): boolean { return this.total <= this.limit; }

  get rangeStart(): number { return this.total ? ((this.page - 1) * this.limit) + 1 : 0; }
  get rangeEnd(): number { return Math.min(this.page * this.limit, this.total); }
  get items(): PaginationItem[] {
    if (this.pages <= 7) return Array.from({ length: Math.max(0, this.pages) }, (_, index) => index + 1);
    const visible = new Set([1, this.pages, this.page - 1, this.page, this.page + 1].filter(value => value >= 1 && value <= this.pages));
    const sorted = [...visible].sort((a, b) => a - b);
    const items: PaginationItem[] = [];
    sorted.forEach((value, index) => {
      if (index && value - sorted[index - 1] > 1) items.push('ellipsis');
      items.push(value);
    });
    return items;
  }

  isNumber(item: PaginationItem): item is number { return typeof item === 'number'; }
  select(next: number): void {
    if (this.disabled || next < 1 || next > this.pages || next === this.page) return;
    this.pageChange.emit(next);
  }
}
